import React, { useState, useEffect } from 'react';
import { Check, ChevronRight, ChevronUp, ChevronDown, Lightbulb, ArrowRight, GripVertical, ThumbsUp } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { PremiumSlider } from '../ui/slider';

// ═══════════════════════════════════════════════════════════════
// STEP RENDERER
// ═══════════════════════════════════════════════════════════════

const SuccessThumb = () => (
  <div 
    className="fixed inset-0 z-[100] flex items-center justify-center pointer-events-none"
    data-testid="success-thumb"
  >
    <div style={{
      animation: 'thumbPop 900ms ease-out forwards',
    }}>
      <div className="w-16 h-16 rounded-full flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #10b981, #059669)', boxShadow: '0 8px 32px rgba(16,185,129,0.4)' }}>
        <ThumbsUp className="w-7 h-7 text-white" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }} />
      </div>
    </div>
    <style>{`
      @keyframes thumbPop {
        0% { opacity: 0; transform: scale(0.3); }
        20% { opacity: 1; transform: scale(1.1); }
        35% { transform: scale(0.95); }
        50% { transform: scale(1); }
        75% { opacity: 1; transform: scale(1); }
        100% { opacity: 0; transform: scale(0.8); }
      }
    `}</style>
  </div>
);

export const StepRenderer = ({ step, onComplete, stepNumber, totalSteps }) => {
  const [answer, setAnswer] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const [isCorrect, setIsCorrect] = useState(null);
  const [showThumb, setShowThumb] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [dragItems, setDragItems] = useState([]);
  const [orderedItems, setOrderedItems] = useState([]);
  const [dropZones, setDropZones] = useState({});
  const [textInputs, setTextInputs] = useState({});
  const [isAnimating, setIsAnimating] = useState(false);
  const [tappedOption, setTappedOption] = useState(null);
  const [sliderValue, setSliderValue] = useState([0]);
  const [shuffledOptions, setShuffledOptions] = useState([]);

  const shuffleArray = (arr) => {
    const shuffled = [...arr];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  useEffect(() => {
    setAnswer(null);
    setShowHint(false);
    setIsCorrect(null);
    setShowThumb(false);
    setAttempts(0);
    setTextInputs({});
    setDropZones({});
    setTappedOption(null);
    setIsAnimating(false);
    setSliderValue([step.sliderDefault || 0]);
    
    if (step.type === 'classify' || step.type === 'dragDrop') {
      setDragItems(shuffleArray(step.items || []));
    }
    if (step.type === 'reorder') {
      setOrderedItems(shuffleArray(step.items || []));
    }
    if (step.options) {
      setShuffledOptions(step.shuffle ? shuffleArray(step.options) : step.options);
    }
  }, [step]);

  const triggerSuccess = (completeData) => {
    setIsCorrect(true);
    setShowThumb(true);
    setTimeout(() => {
      setShowThumb(false);
      onComplete(completeData);
    }, 1000);
  };

  const validateTextInput = (text, required, minLen) => {
    if (!text || text.trim().length < (minLen || 10)) return false;
    if (!required) return true;
    const lowerText = text.toLowerCase();
    return required.every(req => {
      const patterns = req.split('|');
      return patterns.some(p => lowerText.includes(p.toLowerCase()));
    });
  };

  const handleChoice = (option) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setTappedOption(option.id);
    setAnswer(option.id);
    
    setTimeout(() => {
      if (option.correct || option.isChoice) {
        triggerSuccess({ type: 'choice', answer: option.id, correct: true });
      } else {
        setIsCorrect(false);
        setAttempts(prev => prev + 1);
        if (attempts >= 1) setShowHint(true);
        setTimeout(() => {
          setIsAnimating(false);
          setIsCorrect(null);
          setTappedOption(null);
        }, 1500);
      }
    }, 100);
  };

  const moveItem = (fromIndex, toIndex) => {
    const newOrder = [...orderedItems];
    const [moved] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, moved);
    setOrderedItems(newOrder);
  };

  const checkOrder = () => {
    const isOrderCorrect = orderedItems.every((item, idx) => item.order === idx + 1);
    if (isOrderCorrect) {
      triggerSuccess({ type: 'reorder', correct: true });
    } else {
      setIsCorrect(false);
      setAttempts(prev => prev + 1);
      if (attempts >= 1) setShowHint(true);
      setTimeout(() => setIsCorrect(null), 1500);
    }
  };

  const handleDrop = (itemId, category) => {
    setDropZones(prev => ({ ...prev, [itemId]: category }));
  };

  const checkClassification = () => {
    const allCorrect = step.items.every(item => dropZones[item.id] === item.category);
    if (allCorrect) {
      triggerSuccess({ type: 'classify', correct: true });
    } else {
      setIsCorrect(false);
      setAttempts(prev => prev + 1);
      if (attempts >= 1) setShowHint(true);
      setTimeout(() => {
        setIsCorrect(null);
        setDropZones({});
        setDragItems(shuffleArray(step.items || []));
      }, 1500);
    }
  };

  const handleSliderSubmit = () => {
    const isSliderCorrect = sliderValue[0] === step.correctValue;
    if (isSliderCorrect) {
      triggerSuccess({ type: 'slider', answer: sliderValue[0], correct: true });
    } else {
      setIsCorrect(false);
      setAttempts(prev => prev + 1);
      if (attempts >= 1) setShowHint(true);
      setTimeout(() => setIsCorrect(null), 1500);
    }
  };

  const handleTextSubmit = () => {
    const text = textInputs.main || '';
    const isValid = validateTextInput(text, step.requiredElements, step.minLength);
    if (isValid) {
      triggerSuccess({ type: step.type, answer: text, correct: true });
    } else {
      setIsCorrect(false);
      setAttempts(prev => prev + 1);
      if (attempts >= 0) setShowHint(true);
      setTimeout(() => setIsCorrect(null), 1500);
    }
  };

  const handleFillSubmit = () => {
    const text = (textInputs.fill || '').toLowerCase().trim();
    const isValid = step.correctAnswers?.some(ans => text.includes(ans.toLowerCase()));
    if (isValid) {
      triggerSuccess({ type: 'fill', answer: text, correct: true });
    } else {
      setIsCorrect(false);
      setAttempts(prev => prev + 1);
      if (attempts >= 1) setShowHint(true);
      setTimeout(() => setIsCorrect(null), 1500);
    }
  };

  const handleBuildSubmit = () => {
    const allText = Object.values(textInputs).join(' ');
    if (allText.length >= (step.minTotalLength || 50)) {
      triggerSuccess({ type: 'buildPrompt', answer: textInputs, correct: true });
    } else {
      setIsCorrect(false);
      setShowHint(true);
      setTimeout(() => setIsCorrect(null), 1500);
    }
  };

  // ═══ RENDER ═══

  const renderChoice = () => {
    const options = shuffledOptions.length ? shuffledOptions : step.options;
    return (
      <div className="space-y-3">
        {options.map((opt) => (
          <button
            key={opt.id}
            onClick={() => handleChoice(opt)}
            disabled={isAnimating}
            data-testid={`choice-option-${opt.id}`}
            className={`
              w-full p-4 text-left rounded-2xl border-2 
              transition-all duration-200 ease-out cursor-pointer
              ${tappedOption === opt.id ? 'scale-[0.97]' : ''}
              ${answer === opt.id && isCorrect === false
                ? 'border-red-500 bg-red-500/20 text-red-300 animate-shake'
                : answer === opt.id
                  ? 'border-sky-500 bg-sky-500/20 ring-2 ring-sky-500/50'
                  : 'border-slate-600 bg-white/5 hover:border-sky-400 hover:bg-white/10 active:scale-[0.98] text-slate-200'
              }
            `}
          >
            <span className="flex items-center gap-3 text-base">
              <span className="flex-1">{opt.text}</span>
            </span>
          </button>
        ))}
      </div>
    );
  };

  const renderReorder = () => (
    <div className="space-y-2">
      {step.label && <p className="text-base text-white font-medium mb-3">{step.label}</p>}
      {step.question && <p className="text-base text-white font-medium mb-3">{step.question}</p>}
      
      {orderedItems.map((item, idx) => (
        <div
          key={item.id}
          data-testid={`reorder-item-${item.id}`}
          className={`
            flex items-center gap-3 p-4 bg-white/10 border-2 rounded-2xl 
            ${isCorrect === false ? 'border-red-400 bg-red-500/20 animate-shake' : 'border-slate-500'}
          `}
        >
          <div className="flex flex-col gap-1">
            {idx > 0 && (
              <button onClick={() => moveItem(idx, idx - 1)} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors active:scale-90 cursor-pointer" data-testid={`move-up-${item.id}`}>
                <ChevronUp className="w-4 h-4 text-slate-300" />
              </button>
            )}
            {idx < orderedItems.length - 1 && (
              <button onClick={() => moveItem(idx, idx + 1)} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors active:scale-90 cursor-pointer" data-testid={`move-down-${item.id}`}>
                <ChevronDown className="w-4 h-4 text-slate-300" />
              </button>
            )}
          </div>
          <GripVertical className="w-5 h-5 text-slate-400" />
          <span className="flex-1 text-white">{item.text}</span>
          <span className="w-8 h-8 rounded-full bg-sky-500/30 text-sky-300 flex items-center justify-center text-sm font-bold">{idx + 1}</span>
        </div>
      ))}
      <Button onClick={checkOrder} className="w-full mt-4 bg-sky-500 hover:bg-sky-600 rounded-xl h-12 text-base font-medium cursor-pointer" data-testid="check-order-btn">
        Sprawdź kolejność
      </Button>
    </div>
  );

  const renderSlider = () => (
    <div className="space-y-6">
      {step.label && <p className="text-base text-white font-medium">{step.label}</p>}
      {step.question && <p className="text-base text-white font-medium">{step.question}</p>}
      {step.sliderLabels && (
        <div className="text-center">
          <p className="text-sm text-slate-300 mb-2">Przesuń suwak na właściwą pozycję:</p>
        </div>
      )}
      <div className={`p-4 rounded-2xl transition-all duration-300 ${
        isCorrect === false ? 'bg-red-500/20 ring-2 ring-red-400 animate-shake' : 'bg-white/10 border border-slate-500'
      }`}>
        <PremiumSlider
          min={step.sliderMin || 0} max={step.sliderMax || 10} step={step.sliderStep || 1}
          value={sliderValue} onValueChange={setSliderValue}
          showValue={true} showTicks={true} showTooltip={true}
          tickLabels={step.sliderLabels}
          formatValue={(val) => step.sliderLabels?.[val] || `${val}/${step.sliderMax || 10}`}
        />
      </div>
      {step.sliderLabels && sliderValue[0] !== undefined && (
        <div className="text-center p-3 bg-white/10 rounded-xl">
          <p className="text-sm text-slate-300">Twoja odpowiedź:</p>
          <p className="text-lg font-semibold text-white">{step.sliderLabels[sliderValue[0]] || sliderValue[0]}</p>
        </div>
      )}
      <Button onClick={handleSliderSubmit} className="w-full bg-sky-500 hover:bg-sky-600 rounded-xl h-12 text-base font-medium cursor-pointer" data-testid="submit-slider-btn">
        Sprawdź odpowiedź
      </Button>
    </div>
  );

  const renderClassify = () => (
    <div className="space-y-4">
      {step.label && <p className="text-base text-white font-medium mb-2">{step.label}</p>}
      {step.question && <p className="text-base text-white font-medium mb-2">{step.question}</p>}
      <div className="grid grid-cols-2 gap-3">
        {step.categories?.map((cat) => (
          <div key={cat} className={`p-3 rounded-2xl border-2 border-dashed min-h-[100px] ${
            Object.values(dropZones).filter(c => c === cat).length > 0 ? 'border-sky-400 bg-sky-500/20' : 'border-slate-500 bg-white/5'
          }`}>
            <p className="text-sm font-semibold text-white mb-2">{cat}</p>
            <div className="space-y-1.5">
              {Object.entries(dropZones).filter(([_, c]) => c === cat).map(([itemId]) => {
                const item = step.items.find(i => i.id === itemId);
                return (
                  <div key={itemId} className="text-xs p-2.5 bg-white/10 rounded-xl border border-white/20 text-slate-200">{item?.text}</div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <div className="space-y-3">
        <p className="text-sm text-slate-300 font-medium">Elementy do przypisania:</p>
        {dragItems.filter(item => !dropZones[item.id]).map((item) => (
          <div key={item.id} className="p-4 bg-white/10 border-2 border-slate-500 rounded-2xl" data-testid={`classify-item-${item.id}`}>
            <p className="text-sm text-white mb-3">{item.text}</p>
            <div className="flex gap-2 flex-wrap">
              {step.categories?.map(cat => (
                <button key={cat} onClick={() => handleDrop(item.id, cat)} className="text-xs px-4 py-2 bg-sky-500/30 text-sky-300 rounded-full hover:bg-sky-500/50 transition-all active:scale-95 font-medium cursor-pointer" data-testid={`assign-${item.id}-to-${cat}`}>
                  → {cat}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      {Object.keys(dropZones).length === step.items?.length && (
        <Button onClick={checkClassification} className="w-full bg-sky-500 hover:bg-sky-600 rounded-xl h-12 text-base font-medium" data-testid="check-classification-btn">
          Sprawdź odpowiedzi
        </Button>
      )}
    </div>
  );

  const renderTextInput = () => (
    <div className="space-y-4">
      {step.originalPrompt && (
        <div className="p-4 bg-white/10 rounded-2xl border border-white/20">
          <p className="text-xs text-slate-300 mb-1 font-medium">Oryginalny prompt:</p>
          <p className="text-sm italic text-slate-200">"{step.originalPrompt}"</p>
        </div>
      )}
      {step.label && <p className="text-base text-white font-medium">{step.label}</p>}
      {step.question && <p className="text-base text-white font-medium">{step.question}</p>}
      <textarea
        value={textInputs.main || ''}
        onChange={(e) => setTextInputs({ ...textInputs, main: e.target.value })}
        placeholder={step.placeholder || 'Wpisz tutaj...'}
        data-testid="text-input-main"
        className={`w-full h-32 p-4 border-2 rounded-2xl resize-none text-base leading-relaxed bg-white/10 text-white placeholder-slate-400 ${
          isCorrect === false ? 'border-red-400 bg-red-500/20' : 'border-slate-500 focus:border-sky-400'
        }`}
      />
      <div className="flex justify-between items-center text-sm">
        <span className={`font-medium ${(textInputs.main || '').length >= (step.minLength || 10) ? 'text-emerald-400' : 'text-slate-400'}`}>
          {(textInputs.main || '').length} / min {step.minLength || 10} znaków
        </span>
        {step.exampleAnswer && showHint && (
          <span className="text-sky-400 text-xs">Przykład: "{step.exampleAnswer}"</span>
        )}
      </div>
      <Button onClick={handleTextSubmit} className="w-full bg-sky-500 hover:bg-sky-600 rounded-xl h-12 text-base font-medium cursor-pointer" disabled={(textInputs.main || '').length < (step.minLength || 10)} data-testid="submit-text-btn">
        Dalej <ArrowRight className="w-5 h-5 ml-2" />
      </Button>
    </div>
  );

  const renderFill = () => (
    <div className="space-y-4">
      {step.label && <p className="text-base text-white font-medium mb-2">{step.label}</p>}
      {step.question && <p className="text-base text-white font-medium mb-2">{step.question}</p>}
      <p className="text-lg leading-relaxed text-slate-200">
        {step.template?.split('______')[0]}
        <Input
          value={textInputs.fill || ''}
          onChange={(e) => setTextInputs({ ...textInputs, fill: e.target.value })}
          data-testid="fill-input"
          className={`inline-block w-40 mx-2 text-center font-medium rounded-xl border-2 bg-white/10 text-white placeholder-slate-400 ${
            isCorrect === false ? 'border-red-400 bg-red-500/20' : 'border-slate-500'
          }`}
          placeholder="..."
        />
        {step.template?.split('______')[1]}
      </p>
      <Button onClick={handleFillSubmit} className="w-full bg-sky-500 hover:bg-sky-600 rounded-xl h-12 text-base font-medium cursor-pointer" disabled={!(textInputs.fill || '').trim()} data-testid="submit-fill-btn">
        Sprawdź
      </Button>
    </div>
  );

  const renderBuildPrompt = () => (
    <div className="space-y-4">
      {step.label && <p className="text-base text-white font-medium mb-2">{step.label}</p>}
      {step.question && <p className="text-base text-white font-medium mb-2">{step.question}</p>}
      {step.sections?.map((section) => (
        <div key={section}>
          <label className="text-sm font-semibold text-slate-200 mb-2 block">{section}</label>
          <textarea
            value={textInputs[section] || ''}
            onChange={(e) => setTextInputs({ ...textInputs, [section]: e.target.value })}
            className="w-full h-20 p-3 border-2 rounded-xl resize-none border-slate-500 bg-white/10 text-white placeholder-slate-400 focus:border-sky-400"
            placeholder={`Wpisz ${section}...`}
            data-testid={`build-section-${section}`}
          />
        </div>
      ))}
      <div className="p-4 bg-white/10 rounded-2xl border border-white/20">
        <p className="text-xs text-slate-300 mb-2 font-semibold">Twój kompletny prompt:</p>
        <p className="text-sm text-slate-200 leading-relaxed">{Object.values(textInputs).filter(Boolean).join(' ') || '...'}</p>
        <div className="mt-3 flex items-center gap-2">
          <div className="flex-1 h-2 bg-slate-600 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-sky-400 to-emerald-400 rounded-full transition-all duration-500"
              style={{ width: `${Math.min((Object.values(textInputs).join(' ').length / (step.minTotalLength || 50)) * 100, 100)}%` }}
            />
          </div>
          <span className="text-xs text-slate-300 font-medium">{Object.values(textInputs).join(' ').length} / {step.minTotalLength || 50}</span>
        </div>
      </div>
      <Button onClick={handleBuildSubmit} className="w-full bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-600 hover:to-emerald-600 rounded-xl h-12 text-base font-medium cursor-pointer" disabled={Object.values(textInputs).join(' ').length < (step.minTotalLength || 50)} data-testid="submit-build-btn">
        Złóż prompt <Check className="w-5 h-5 ml-2" />
      </Button>
    </div>
  );

  const renderAICoachIntro = () => (
    <div className="space-y-4">
      <div className="p-6 rounded-2xl text-center" style={{ background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.2), rgba(16, 185, 129, 0.2))', border: '1px solid rgba(14, 165, 233, 0.3)' }}>
        <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0ea5e9, #10b981)', boxShadow: '0 8px 25px rgba(14, 165, 233, 0.3)' }}>
          <Lightbulb className="w-8 h-8 text-white" />
        </div>
        <p className="text-slate-300 text-base leading-relaxed mb-4">{step.content}</p>
      </div>
      <Button
        onClick={() => {
          window.dispatchEvent(new CustomEvent('openAICoach'));
          setTimeout(() => triggerSuccess({ type: 'aiCoachIntro', correct: true }), 500);
        }}
        className="w-full bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-600 hover:to-emerald-600 rounded-xl h-14 text-base font-semibold cursor-pointer"
        data-testid="open-ai-coach-btn"
      >
        {step.buttonText || 'Otwórz Mentora'} <ArrowRight className="w-5 h-5 ml-2" />
      </Button>
    </div>
  );

  // ═══ MAIN RENDER ═══

  return (
    <Card
      className="shadow-sm border-0"
      style={{ background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(12px)' }}
      data-testid="step-card"
    >
      <CardContent className="pt-5 pb-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold bg-sky-500 text-white">
              {stepNumber}
            </span>
            <span className="text-sm text-slate-400 font-medium">/ {totalSteps}</span>
          </div>
        </div>

        <h3 className="font-bold text-white mb-2 text-lg">{step.title}</h3>
        <p className="text-slate-300 mb-5 whitespace-pre-wrap leading-relaxed">{step.content}</p>

        <div>
          {step.type === 'choice' && renderChoice()}
          {step.type === 'reorder' && renderReorder()}
          {step.type === 'slider' && renderSlider()}
          {step.type === 'classify' && renderClassify()}
          {step.type === 'dragDrop' && renderClassify()}
          {(step.type === 'shortPrompt' || step.type === 'fixPrompt') && renderTextInput()}
          {step.type === 'fill' && renderFill()}
          {step.type === 'buildPrompt' && renderBuildPrompt()}
          {step.type === 'aiCoachIntro' && renderAICoachIntro()}
        </div>

        {showHint && step.softHint && (
          <div className="mt-5 p-4 rounded-2xl flex items-start gap-3 animate-slideUp" style={{ background: 'rgba(245, 158, 11, 0.15)', border: '1px solid rgba(245, 158, 11, 0.3)' }}>
            <div className="w-8 h-8 rounded-full bg-amber-500/30 flex items-center justify-center flex-shrink-0">
              <Lightbulb className="w-4 h-4 text-amber-400" />
            </div>
            <p className="text-sm text-amber-300 leading-relaxed">{step.softHint}</p>
          </div>
        )}
      </CardContent>

      {showThumb && <SuccessThumb />}
    </Card>
  );
};

export default StepRenderer;
